import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, ButtonStyle } from '@novo-electronique/react-button';
import classNames from 'classnames';

import { FileUploadedState, IFileUploaded } from './file-uploaded';
import DefaultFileUploaded, { FileUploadedAction, IFileUploadedProps } from './FileUploaded';
import { ReactComponent as Upload } from './icons/upload.svg';

interface IProps {
  files?: IFileUploaded[];
  upload: (file: IFileUploaded) => Promise<string>;
  remove?: (file: IFileUploaded) => Promise<void>;
  download?: (file: IFileUploaded) => Promise<void>;
  onClick?: (file: IFileUploaded) => void;
  onChange?: (files: IFileUploaded[], isUpdating: boolean) => void;
  accept?: string | string[];
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  labelsNamespace?: string;
  fileUploadedComponent?: FunctionComponent<IFileUploadedProps>;
}

function Dropzone({
  files: initialFiles,
  accept,
  upload,
  download,
  remove,
  onClick,
  onChange,
  maxFiles,
  multiple,
  disabled,
  labelsNamespace = 'dropzone',
  fileUploadedComponent: FileUploaded = DefaultFileUploaded,
}: IProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<IFileUploaded[]>(initialFiles || []);
  const [isUploading, setUploading] = useState(false);

  const activeUploadCount = useRef(0);
  const initial = useRef(true);

  useEffect(() => {
    if (initial.current) {
      initial.current = false;
      return;
    }

    if (onChange) {
      onChange(files, isUploading);
    }
  }, [files, isUploading]);

  const onFilesUploading = useCallback((acceptedFiles: IFileUploaded[]) => {
    if (activeUploadCount.current++ === 0) {
      setUploading(true);
    }

    setFiles((files) => [...files, ...acceptedFiles]);

    const updateFile = (currentFile: IFileUploaded, changes: Partial<IFileUploaded>) => {
      setFiles((files) => {
        const filesToUpdate = [...files];
        const index = filesToUpdate.findIndex((file) => file === currentFile);
        filesToUpdate[index] = Object.assign(currentFile, changes);

        return filesToUpdate;
      });
    };

    Promise.allSettled(
      acceptedFiles.map((fileToUpload) =>
        upload(fileToUpload)
          .then((id) => updateFile(fileToUpload, { id, state: FileUploadedState.Completed }))
          .catch(() => updateFile(fileToUpload, { state: FileUploadedState.Faulty })),
      ),
    ).then(() => {
      if (--activeUploadCount.current === 0) {
        setUploading(false);
      }
    });
  }, []);

  const onRemove = (fileToRemove: IFileUploaded) => {
    if (confirm(t(`${labelsNamespace}.fileRemoveConfirmation`))) {
      setFiles((files) => files.filter((file) => file !== fileToRemove));

      if (remove && fileToRemove.id) {
        remove(fileToRemove);
      }
    }
  };

  const maxFilesReached = maxFiles && maxFiles <= files.length;
  const isZoneDisabled = disabled || maxFilesReached;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: isZoneDisabled,
    onDrop: onFilesUploading,
    accept,
    maxFiles,
    multiple,
  });

  const acceptedFiles = Array.isArray(accept) ? accept : [accept];
  const className = classNames('novoDropzone', {
    'novoDropzone--disabled': isZoneDisabled,
    'novoDropzone--is-drag-active': isDragActive,
  });
  return (
    <div className={className}>
      <div {...getRootProps({ className: 'novoDropzone__zone' })}>
        <input {...getInputProps()} />
        <Upload className="novoDropzone__zone__icon" />
        <p className="novoDropzone__zone__directives">{t(`${labelsNamespace}.instructions`)}</p>
        <p className="novoDropzone__zone__or">{t(`${labelsNamespace}.or`)}</p>
        <Button size={ButtonSize.Small} style={ButtonStyle.Action} disabled={isZoneDisabled}>
          {t(`${labelsNamespace}.browse`)}
        </Button>
        {accept && (
          <p className="novoDropzone__accepted-files">
            {t(`${labelsNamespace}.acceptedFiles`, {
              count: acceptedFiles.length,
              files: acceptedFiles
                .map((file) => file.replace('.', '').toUpperCase())
                .sort()
                .join(', '),
            })}
          </p>
        )}
      </div>

      {files.length > 0 && (
        <ul className="novoDropzone__files-uploaded">
          {files.map((file, i) => (
            <li className="novoDropzone__files-uploaded__item" key={i}>
              <FileUploaded
                file={file}
                onClick={onClick}
                disabled={disabled}
                labelsNamespace={labelsNamespace}
                actions={{
                  [FileUploadedAction.Download]: download,
                  [FileUploadedAction.Remove]: onRemove,
                }}
                withStatus
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type { IProps as IDropzoneProps };
export default Dropzone;
